import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, HStack, Text, IconButton, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

export interface BlockPlaylist {
  playlistId: string;
  playlistName: string;
  trackCount: number;
}

interface SortablePlaylistListProps {
  playlists: BlockPlaylist[];
  onReorder: (orderedPlaylistIds: string[]) => void;
  onRemove: (playlistId: string) => void;
}

function Row({ playlist, onRemove }: { playlist: BlockPlaylist; onRemove: (id: string) => void }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: playlist.playlistId,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <HStack ref={setNodeRef} style={style} py={2} px={2} spacing={3} bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="md">
      <Box {...attributes} {...listeners} cursor="grab" color="gray.300" fontSize="lg" lineHeight={1} _hover={{ color: 'gray.500' }} aria-label={t('playlists.dragHandle')}>
        ⠿
      </Box>
      <Box flex={1} minW={0}>
        <Text fontWeight="500" color="gray.800" noOfLines={1}>
          {playlist.playlistName}
        </Text>
        <Text fontSize="xs" color="gray.400">
          {t('playlists.trackCountLabel', { count: playlist.trackCount })}
        </Text>
      </Box>
      <IconButton
        aria-label={t('common.delete')}
        icon={<Text fontSize="sm">✕</Text>}
        size="xs"
        variant="ghost"
        color="gray.400"
        _hover={{ color: 'red.500', bg: 'red.50' }}
        onClick={() => onRemove(playlist.playlistId)}
      />
    </HStack>
  );
}

export function SortablePlaylistList({ playlists, onReorder, onRemove }: SortablePlaylistListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = playlists.map((p) => p.playlistId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <VStack align="stretch" spacing={2}>
          {playlists.map((p) => (
            <Row key={p.playlistId} playlist={p} onRemove={onRemove} />
          ))}
        </VStack>
      </SortableContext>
    </DndContext>
  );
}

export default SortablePlaylistList;
